import { isNumber, sum } from 'lodash';
import type { Question } from 'schema/poll';
import type { AnswerState } from 'schema/state';

const isMultipleQuestion = (question: Question) => {
  return (
    question.data.type === 'POLL_MULTIPLE' ||
    question.data.type === 'POLL_MULTIPLE_FROM_PICTURE' ||
    question.data.type === 'POLL_MULTIPLE_SELECT' ||
    question.data.type === 'MATRIX_POLL_MULTIPLE'
  );
};

const isQuestionWithAwardAmount = (question: Question) => {
  return isMultipleQuestion(question) || question.data.type === 'POLL_TEXTAREA' || question.data.type === 'POLL_RANK';
};

export function calcAwardAmount(question: Question, answeredIds: string[], isCorrect: boolean) {
  const hasAward = 'hasAward' in question.data ? question.data.hasAward : false;
  const hasCorrectAnswer = 'hasCorrectAnswer' in question.data ? question.data.hasCorrectAnswer : false;
  const awardAmount = 'awardAmount' in question.data ? question.data.awardAmount : 0;
  const answers = 'answers' in question.data ? question.data.answers : [];

  const isRating =
    question.data.type === 'POLL_RATING_DIGITS' ||
    question.data.type === 'POLL_RATING_LIKE' ||
    question.data.type === 'POLL_RATING_SLIDER' ||
    question.data.type === 'POLL_RATING_SMILIES' ||
    question.data.type === 'POLL_RATING_STARS' ||
    question.data.type === 'MATRIX_POLL_RATING_DIGITS' ||
    question.data.type === 'MATRIX_POLL_RATING_STARS';

  if (!hasAward || isRating) return 0;

  if (isQuestionWithAwardAmount(question) && hasCorrectAnswer) {
    return isCorrect ? awardAmount : 0;
  }

  return answeredIds.reduce((acc, id) => {
    const answer = answers.find((it: any) => it.id === id);
    return acc + (answer && 'awardAmount' in answer && isNumber(answer.awardAmount) ? answer.awardAmount : 0);
  }, 0);
}

export function calcMaxAwardAmount(question: Question, answersIds: string[]): number {
  if (!question.data) return 0;

  if (!('hasAward' in question.data) || !question.data.hasAward) return 0;
  const { answers = [] } = question.data;

  const hasCorrectAnswer = 'hasCorrectAnswer' in question.data ? question.data.hasCorrectAnswer : false;
  const awardAmount = 'awardAmount' in question.data ? question.data.awardAmount ?? 0 : 0;

  const scores = answersIds.map(id => {
    const answer = answers.find(it => it.id === id);
    if (answer && 'awardAmount' in answer && typeof answer.awardAmount === 'number') {
      return answer.awardAmount ?? 0;
    }
    return 0;
  });

  if (isQuestionWithAwardAmount(question) && hasCorrectAnswer) {
    return awardAmount;
  }

  /** Для вопросов с множественным выбором, находим максимальную сумму баллов за показанные ответы */
  if (isMultipleQuestion(question)) {
    let limit = answersIds.length;

    if ('answerCountMaximum' in question.data && question.data.answerCountMaximum) {
      limit = question.data.answerCountMaximum;
    }

    if (limit === answersIds.length) {
      return sum(scores);
    }

    return sum(scores.sort((a, b) => b - a).slice(0, limit));
  }

  return Math.max(...scores);
}

export function calcPollMaxAwardAmount(questions: Question[], answersState: AnswerState[]) {
  let maxAwardAmount = 0;
  const processedQuestionIds = new Set<string>();

  for (const state of answersState.slice().reverse()) {
    const question = questions.find(it => it.id === state.id);

    if (!processedQuestionIds.has(state.id) && question) {
      maxAwardAmount += calcMaxAwardAmount(question, state.answersIds ?? []);
      processedQuestionIds.add(state.id);
    }
  }

  return maxAwardAmount;
}
